import type { ButtonHTMLAttributes } from 'react';
import React, { useId } from 'react';

import type { DynamicIconName } from '../../assets/DynamicIcon/DynamicIcon';
import type { DropdownMenuItemProps } from '../../components/DropdownMenu/DropdownMenu';
import type { PolymorphicComponentProps } from '../../utilities/types/polymorphicAsProp';
import { DynamicIcon } from '../../assets/DynamicIcon/DynamicIcon';
import { Icon } from '../../assets/Icon/Icon';
import { Badge } from '../../components/Badge/Badge';
import { BadgeGroup } from '../../components/Badge/BadgeGroup';
import {
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPopover,
} from '../../components/DropdownMenu/DropdownMenu';
import { colors, darkThemeSelector, fontWeights, shadows, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { useViewport } from '../../utilities/useViewport';
import { Button } from '../Button/Button';
import { ControlGroup } from '../ControlGroup/ControlGroup';
import { selectors } from '../shared/styles';

const BreadcrumbHomeIcon = styled(Icon, {
  width: '$14',
  height: '$14',
  color: '$$iconColor',
});

const BreadcrumbLabel = styled(Text, {
  color: '$$labelColor',
  fontWeight: fontWeights.bold,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  '@notDesktop': {
    ...SmallSansSizes,
  },

  '@desktop': {
    ...BodySansSizes,
  },

  variants: {
    selected: {
      true: {
        fontWeight: fontWeights.extraBold,
      },
      false: {},
    },
  },
});

const BreadcrumbPageContainer = styled('span', {
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '$6',
  padding: '$4 $6',
  minWidth: 0,
  borderRadius: '$8',
  $$labelColor: colors.bodyNeutralLight,
  $$iconColor: colors.iconNeutralLight,

  [darkThemeSelector]: {
    $$labelColor: colors.bodyNeutralDark,
    $$iconColor: colors.iconNeutralDark,
  },

  [selectors.hover]: {
    $$labelColor: colors.headingNeutralLight,
    $$iconColor: colors.bodyNeutralLight,

    [darkThemeSelector]: {
      $$labelColor: colors.headingNeutralDark,
      $$iconColor: colors.bodyNeutralDark,
    },
  },

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    hasLink: {
      true: {
        cursor: 'pointer',

        [selectors.focus]: {
          zIndex: 3,
          boxShadow: shadows.focusRingLight,

          [darkThemeSelector]: {
            boxShadow: shadows.focusRingDark,
          },
        },
      },
      false: {
        cursor: 'default',
      },
    },
    onClick: {
      true: {},
    },
    selected: {
      true: {
        zIndex: 2,
        $$labelColor: colors.bodyBrandLight,
        $$iconColor: colors.iconBrandLight,

        [darkThemeSelector]: {
          $$labelColor: colors.bodyBrandDark,
          $$iconColor: colors.iconBrandDark,
        },
      },
      false: {
        zIndex: 1,
      },
    },
  },
});

const BreadcrumbFieldContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  minWidth: 0,
  justifyContent: 'center',
  alignItems: 'center',

  '@notDesktop': {
    width: '100%',
    gap: '$8',
  },

  '@desktop': {
    gap: '$6',
  },
});

const BreadcrumbStepper = styled(Icon, {
  width: '$8',
  height: '$8',
  color: colors.iconNeutralLight,

  [darkThemeSelector]: {
    color: colors.iconNeutralDark,
  },

  '&:last-child': {
    display: 'none',
  },
});

export interface BreadcrumbPageProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The annotation of the breadcrumb page.
   */
  annotation?: React.ReactNode;
  /**
   * The badges of the breadcrumb page.
   */
  badges?: React.ReactNode;
  /**
   * Whether the breadcrumb page has a child.
   */
  hasChild?: boolean;
  /**
   * The icon of the breadcrumb page.
   */
  icon?: DynamicIconName;
  /**
   * The label of the breadcrumb page.
   */
  label?: React.ReactNode;
  /**
   * Whether the breadcrumb page is selected.
   */
  selected?: boolean;

  // Routing
  as?: any;
  to?: any;
  // Canonical non-interpolated version of `to` (without filled in params)
  path?: string;
}

export function BreadcrumbPage<Tag extends React.ElementType>({
  annotation,
  badges,
  hasChild = false,
  icon,
  label,
  selected,
  ref,
  // Routing
  as,
  to,
  ...remaining
}: PolymorphicComponentProps<Tag, BreadcrumbPageProps>) {
  return (
    <>
      <BreadcrumbPageContainer
        as={as}
        to={to}
        hasLink={!!to}
        tabIndex={to ? 0 : undefined}
        ref={ref}
        role="tab"
        aria-label={icon ? label : undefined}
        aria-selected={selected ? 'true' : 'false'}
        selected={selected}
        {...(remaining as object)}
      >
        {icon && <DynamicIcon icon={icon} size={18} variant={selected ? 'brand' : 'neutral'} />}
        {label && <BreadcrumbLabel selected={selected}>{label}</BreadcrumbLabel>}
        {annotation && (
          <Badge size="small" variant={selected ? 'brand' : 'neutral'}>
            {annotation}
          </Badge>
        )}
        {badges && <BadgeGroup relation="separate">{badges}</BadgeGroup>}
      </BreadcrumbPageContainer>
      {hasChild && <BreadcrumbStepper icon="chevron-right" />}
    </>
  );
}

export interface BreadcrumbFieldProps {
  badges?: React.ReactNode;
  field: React.ReactNode;
  hasChild?: boolean;
}

function BreadcrumbField({ badges, field, hasChild = false }: BreadcrumbFieldProps) {
  return (
    <>
      <BreadcrumbFieldContainer>
        {field}
        {badges && <BadgeGroup relation="separate">{badges}</BadgeGroup>}
      </BreadcrumbFieldContainer>
      {hasChild && <BreadcrumbStepper icon="chevron-right" />}
    </>
  );
}
export interface BreadcrumbProps {
  field?: BreadcrumbFieldProps;
  page?: BreadcrumbPageProps;
  hasChild?: boolean;
  hideOnMobile?: boolean;
}

export function Breadcrumb({
  field,
  page,
  hasChild = false,
  hideOnMobile = true,
}: BreadcrumbProps) {
  const { breakpoint } = useViewport();
  if (hideOnMobile && breakpoint !== 'desktop') {
    return null;
  }

  if (field) {
    return <BreadcrumbField hasChild={hasChild} {...field} />;
  }
  if (page) {
    return <BreadcrumbPage hasChild={hasChild} {...page} />;
  }
  return null;
}

const BreadcrumbsCrumbs = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$2',

  '@notDesktop': {
    width: '100%',
  },
});

const BreadcrumbsContainer = styled('nav', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '$6',

  '@notDesktop': {
    width: '100%',
  },
});

export type BreadcrumbsPropBack = React.MouseEventHandler<HTMLButtonElement>;
export type BreadcrumbsPropHome = Omit<BreadcrumbPageProps, 'label'>;
export type BreadcrumbsPropHistory = DropdownMenuItemProps;

export type BreadcrumbsProps = {
  back?: BreadcrumbsPropBack;
  crumbs?: BreadcrumbProps[];
  home?: BreadcrumbsPropHome;
  history?: BreadcrumbsPropHistory[];
};

export function Breadcrumbs({ back, crumbs, home, history, ...remaining }: BreadcrumbsProps) {
  const uuid = useId();
  return (
    <BreadcrumbsContainer aria-label="Breadcrumbs" role="tablist" {...remaining}>
      {(history || back) && (
        <ControlGroup relation="joined" size="small" variant="secondary">
          {history && history.length > 0 && (
            <DropdownMenu>
              <DropdownMenuButton arrangement="hidden-label" icon="clock">
                History
              </DropdownMenuButton>
              <DropdownMenuPopover>
                <DropdownMenuGroup label="History">
                  {history.map((historyItem) => (
                    <DropdownMenuItem {...historyItem} />
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuPopover>
            </DropdownMenu>
          )}
          {back && (
            <Button arrangement="hidden-label" icon="chevron-left" onClick={back}>
              Back
            </Button>
          )}
        </ControlGroup>
      )}
      {(home || crumbs) && (
        <BreadcrumbsCrumbs>
          {home && <BreadcrumbPage {...home} label={<BreadcrumbHomeIcon icon="home" />} hasChild />}
          {crumbs &&
            crumbs.map((crumb, index) => (
              <Breadcrumb
                key={`breadcrumb-${crumb.field ? 'field' : crumb.page?.label}-${uuid}`}
                hasChild={index !== crumbs.length - 1}
                {...crumb}
              />
            ))}
        </BreadcrumbsCrumbs>
      )}
    </BreadcrumbsContainer>
  );
}
